import arrowUpIcon from "@assets/images/arrow-up-white.svg";
import folderIcon from "@assets/images/folder-open-new.svg";
import infoIcon from "@assets/images/info.svg";
import warningIcon from "@assets/images/warning-red.svg";
import { Box, Button, CircularProgress, Typography } from "@material-ui/core";
import DialogProcessing from "@saleor/components/DialogProcessing/DialogProcessing";
import MyTypography from "@saleor/components/MyTypography";
import { useUploadAlternativeMaterialsMutation } from "@saleor/graphql";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { FileError, useDropzone } from "react-dropzone";
import SVG from "react-inlinesvg";
import { useIntl } from "react-intl";
import FileLine from "src/alternatedMaterial/components/UploadFile/FileLine";

import DialogErrorLine, { IError, IRefDialog } from "./DialogErrorLine";
import DialogSuccess, { IRefDialogSuccess } from "./DialogSuccess";
import useStyle from "./styles";

interface IFile {
  size: number;
  type: string;
  path: string;
  name: string;
}

interface IUploadFile {
  onUploadSuccess?: () => void;
}

export default ({ onUploadSuccess }: IUploadFile) => {
  const classes = useStyle();
  const intl = useIntl();

  const {
    getRootProps,
    getInputProps,
    acceptedFiles,
    fileRejections
  } = useDropzone({
    multiple: false,
    accept: ".xlsx",
    maxSize: 10000000
  });

  const [file, setFile] = useState<IFile>();
  const [openDialogProcessing, setOpenDialogProcessing] = useState(false);

  const dialogErrorRef = useRef<IRefDialog | null>(null);
  const dialogSuccessRef = useRef<null | IRefDialogSuccess>(null);
  const [errorLines, setErrorLines] = useState<IError[] | null>();
  const onRemove = () => {
    setFile(null);
    setErrorLines(null);
  };

  const [uploadFile, uploadState] = useUploadAlternativeMaterialsMutation({
    onCompleted: data => {
      setOpenDialogProcessing(false);
      if (!!data?.uploadAlternativeMaterials?.status) {
        dialogSuccessRef.current.onOpen(file?.name);
        setFile(null);
        setErrorLines(null);
        if (onUploadSuccess) {
          onUploadSuccess();
        }
      } else {
        const errors = data?.uploadAlternativeMaterials?.errors;
        setErrorLines(errors);
        dialogErrorRef.current.onOpen(errors);
      }
    },
    onError: () => {
      setOpenDialogProcessing(false);
    },
    errorPolicy: "all"
  });

  const onExplainError = () => {
    dialogErrorRef.current.onOpen(errorLines);
  };

  const onFileReady = () => {
    let result: any;
    if (acceptedFiles.length > 0) {
      result = acceptedFiles[0];
    } else if (fileRejections.length > 0) {
      result = fileRejections[0].file;
    }
    setFile(result);
    setErrorLines(null);
  };

  useEffect(() => {
    onFileReady();
  }, [acceptedFiles, fileRejections]);

  const errorMessage = useMemo<Pick<FileError, "code"> | string>(() => {
    if (!!fileRejections[0]?.errors && file) {
      return fileRejections[0]?.errors[0].code;
    }
    return "";
  }, [fileRejections, file]);

  const canUpload = !errorMessage && !!file;

  const onUpload = () => {
    if (canUpload) {
      setOpenDialogProcessing(true);
      uploadFile({ variables: { file } });
    }
  };

  const body = (
    <Box
      display={"flex"}
      flexDirection="column"
      flex={1}
      data-testid="upload-box"
    >
      <section className={classes.uploadBox} id="upload-box">
        <Box
          {...getRootProps({ className: "dropzone" })}
          flexDirection="column"
          alignItems={"center"}
          height="93%"
          justifyContent="center"
          display={"flex"}
        >
          <input
            {...getInputProps()}
            id="input-upload-file"
            name="input-upload-file"
            data-testid="input-upload-file"
          />
          <SVG src={folderIcon} />
          <Typography className={classes.dragTitle}>
            Drag & Drop to upload file or
          </Typography>
          <Typography component={"u"} className={`${classes.browse} `}>
            Click here to browse
          </Typography>
        </Box>
      </section>
      <FileLine path={file?.path} onRemove={onRemove} />
    </Box>
  );

  const BottomMessage = () => {
    const infoMessage1 = intl.formatMessage({
      defaultMessage: "รองรับไฟล์นามสกุล",
      description: "รองรับไฟล์นามสกุล"
    });

    const infoMessage3 = intl.formatMessage({
      defaultMessage: "เท่านั้น",
      description: "เท่านั้น"
    });
    const infoMessage = () => (
      <>
        {infoMessage1} <strong>.xlsx</strong> {infoMessage3}
      </>
    );

    const errorLineMessage = intl.formatMessage({
      defaultMessage: "อัปโหลดไฟล์ไม่สำเร็จ",
      description: "errorLineMessage"
    });

    const explainMessage = intl.formatMessage({
      defaultMessage: "ดูข้อมูลเพิ่มเติม",
      description: "explainMessage"
    });
    const uploadedMessage1 = intl.formatMessage({
      defaultMessage: "กรุณากด",
      description: "uploadedMessage1"
    });
    const uploadedMessage3 = intl.formatMessage({
      defaultMessage: "เพื่อยืนยัน",
      description: "uploadedMessage3"
    });

    const uploadedMessage = () => (
      <>
        {uploadedMessage1} <strong>Upload File</strong> {uploadedMessage3}
      </>
    );

    const getErrorMessage = () => {
      const invalidMessage1 = intl.formatMessage({
        defaultMessage: "รองรับไฟล์นามสกุล",
        description: "invalidMessage1"
      });
      const invalidMessage2 = intl.formatMessage({
        defaultMessage: "เท่านั้น, กรุณาลองใหม่",
        description: "invalidMessage2"
      });

      const invalidMessage = () => (
        <>
          {invalidMessage1} <strong>.xlsx</strong> {invalidMessage2}
        </>
      );
      if (errorMessage === "file-too-large") {
        return intl.formatMessage({
          defaultMessage: "ไฟล์มีขนาดใหญ่เกิน 10MB กรุณาลองใหม่อีกครั้ง",
          description: "fileTooLarge"
        });
      }
      if (errorMessage === "file-invalid-type") {
        return invalidMessage();
      }
      return fileRejections[0]?.errors[0]?.message;
    };

    const isShowWarningIcon = errorMessage || errorLines;

    return (
      <Box display={"flex"} justifyContent={"space-between"}>
        <Box alignItems="center" display="flex" justifyContent={"center"}>
          <SVG
            src={isShowWarningIcon ? warningIcon : infoIcon}
            className={classes.infoIcon}
            style={{ marginTop: 0 }}
          />
          {errorLines && (
            <React.Fragment>
              <Typography className={classes.note}>
                {errorLineMessage} &nbsp;
              </Typography>
              <Typography
                onClick={onExplainError}
                component={"u"}
                style={{ fontWeight: 700 }}
                className={classes.notes}
              >
                {explainMessage}
              </Typography>
            </React.Fragment>
          )}
          {!errorLines && (
            <Typography
              className={classes.note}
              style={{
                color: errorMessage ? "#E41B17" : "#555555"
              }}
            >
              {errorMessage
                ? getErrorMessage()
                : !canUpload
                ? infoMessage()
                : ""}
              {canUpload ? uploadedMessage() : " "}
            </Typography>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Box
      className={classes.container}
      display={"flex"}
      flexDirection={"column"}
    >
      <MyTypography variant="subtitle1" className={classes.uploadTitle}>
        <span className={classes.uploadTitleBold}>Upload :</span> Master -
        Alternated Material File
      </MyTypography>
      {body}
      <Box display={"flex"} justifyContent={"space-between"} marginTop={"0px"}>
        <BottomMessage />
        <Button
          className={classes.button}
          onClick={onUpload}
          style={{
            backgroundColor: canUpload ? "#009FB8" : "#E0E0E0",
            cursor: canUpload ? "pointer" : "not-allowed",
            height: "40px"
          }}
          data-testid="upload-button"
        >
          {uploadState.loading ? (
            <CircularProgress size={16} />
          ) : (
            <SVG src={arrowUpIcon} />
          )}

          <Typography className={classes.buttonTitle}>
            {intl.formatMessage({
              defaultMessage: "Upload File"
            })}
          </Typography>
        </Button>
      </Box>
      <DialogErrorLine ref={dialogErrorRef} />
      <DialogSuccess ref={dialogSuccessRef} />
      <DialogProcessing
        open={openDialogProcessing}
        onClose={() => setOpenDialogProcessing(false)}
        title="ระบบกำลังอัปโหลดไฟล์"
        description="กรุณารอสักครู่ / โปรดอย่ากด Refresh หรือปิดหน้าจอ"
      />
    </Box>
  );
};
